import {
  ArrowRight,
  ArrowUpFromLine,
  CloudCog,
  Code2,
  Database,
  ShieldCheck,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";

type Service = {
  Icon: LucideIcon;
  petal: string;
  tint: string;
  title: string;
  body: string;
  /** Only present on cards that point at a real section further down the
   *  page. Cards without dedicated elaboration don't show a fake link. */
  more?: { label: string; href: string };
};

const SERVICES: Service[] = [
  {
    Icon: Code2,
    petal: "var(--petal-river)",
    tint: "rgba(31,102,200,.10)",
    title: "New software, built right",
    body: "From a one-screen tool to a full production app. Modern stack, clear handoff, docs included.",
  },
  {
    Icon: ArrowUpFromLine,
    petal: "var(--petal-coral)",
    tint: "rgba(229,69,62,.10)",
    title: "Legacy migrations",
    body: "VB6, Access, classic ASP, ColdFusion, FoxPro — your old app keeps running while the new one rolls in.",
    more: { label: "How we modernize", href: "#modernize" },
  },
  {
    Icon: CloudCog,
    petal: "var(--petal-sky)",
    tint: "rgba(0,166,223,.12)",
    title: "Move to the cloud",
    body: "On-prem servers to AWS or Azure. Lift, refactor, or rebuild — whichever fits the budget.",
  },
  {
    Icon: Database,
    petal: "var(--mtn-700)",
    tint: "var(--mtn-100)",
    title: "Production databases",
    body: "Postgres and SQL Server, built and maintained. Backups, monitoring, the boring stuff done well.",
  },
  {
    Icon: Workflow,
    petal: "var(--petal-leaf)",
    tint: "rgba(91,199,66,.12)",
    title: "Scripts & integrations",
    body: "QuickBooks to Shopify, CSV nightmares, API glue. Small automations that pay for themselves.",
  },
  {
    Icon: ShieldCheck,
    petal: "var(--petal-sun)",
    tint: "rgba(245,179,36,.14)",
    title: "AI build review",
    body: "AI-generated apps are running real businesses now. Before yours does — or after, if it's already live — we audit the code, dependencies, security, and data handling. Move at the speed of AI without skipping the safety work.",
    more: { label: "See pricing", href: "/pricing#ai-build-review" },
  },
];

export function Services() {
  return (
    <section className="section" id="services">
      <div className="container">
        <div className="section-head reveal">
          <span className="eyebrow">What we do</span>
          <h2>
            The unglamorous plumbing,
            <br />
            <em>done well.</em>
          </h2>
          <p>
            One shop for the work most small businesses don't have time to figure
            out. Pick a piece or hand us the whole stack.
          </p>
        </div>

        <div className="services-grid">
          {SERVICES.map((s) => (
            <article className="service-card reveal" key={s.title}>
              <span className="stripe" style={{ background: s.petal }} />
              <div
                className="icon-wrap"
                style={{ background: s.tint, color: s.petal }}
              >
                <s.Icon size={22} />
              </div>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
              {s.more ? (
                <Link className="more" to={s.more.href}>
                  {s.more.label} <ArrowRight size={14} />
                </Link>
              ) : null}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
